import React, { useState, useEffect } from "react";
import axios from "axios";
import qs from "qs";
import { useNavigate } from "react-router-dom";

function PackageBookingPopup({
  pkg,
  isWithFlight,
  withFlightTotalSalePrice,
  doublePrice,
  hotel_ratings,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    hotel_rating: pkg?.hotel_ratings?.length ? String(pkg.hotel_ratings[0].rating) : "",
    flight_option: isWithFlight ? "with_flight" : "without_flight",
    booking_from: "",
  });

  // Rooms default to empty string so "Select" option is selected by default
  const [rooms, setRooms] = useState([
    {
      room_type: "",
      adults: "0",
      cnb: "N/A",
      cwb: "N/A",
      infant: "0",
      travelers: []
    },
  ]);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (user?.id) {
      setForm((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, []);
  const [errors, setErrors] = useState({});
  const [serverMessage, setServerMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [roomsTotal, setRoomsTotal] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(0);

  // Coupon states
  const [coupons, setCoupons] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState("");
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [tcsAmount, setTcsAmount] = useState(0);
  useEffect(() => {
    axios
      .get("https://online.funforay.com/admin/api/coupons")
      .then((res) => {
        if (res.data?.success) {
          setCoupons(res.data.data || []);
        }
      })
      .catch((err) => console.error("Coupon fetch error", err));
  }, []);

  

  const getTravelerLabels = (room) => {
    const labels = [];
    if (!room) return labels;
    const adults = Number(room.adults) || 0;
    const cnb = room.cnb === "N/A" ? 0 : Number(room.cnb) || 0;
    const cwb = room.cwb === "N/A" ? 0 : Number(room.cwb) || 0;
    const infant = room.infant === "N/A" ? 0 : Number(room.infant) || 0;

    for (let i = 1; i <= adults; i++) labels.push(`Adult ${i}`);
    for (let i = 1; i <= cnb; i++) labels.push(`Child (CNB) ${i}`);
    for (let i = 1; i <= cwb; i++) labels.push(`Child (CWB) ${i}`);
    for (let i = 1; i <= infant; i++) labels.push(`Infant ${i}`);

    return labels;
  };

  const parsePrice = (p) => {
    if (p == null) return 0;
    const s = String(p).trim();
    if (!s || s.toLowerCase() === "n/a") return 0;
    const num = parseFloat(s.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const getRoomTypesByRating = (rating) => {
    if (!pkg?.hotel_ratings) return [];
    const r = pkg.hotel_ratings.find((hr) => String(hr.rating) === String(rating));
    return r?.room_types || [];
  };

  const findRateRow = (ratingValue, roomTypeValue) => {
    if (!pkg?.hotel_ratings) return null;
    const ratingObj = pkg.hotel_ratings.find((hr) => String(hr.rating) === String(ratingValue));
    if (!ratingObj) return null;
    return ratingObj.room_types?.find((rt) => String(rt.room_type) === String(roomTypeValue)) || null;
  };

  const getAdultsOptions = (roomType) => {
    if (!roomType) return [];
    if (roomType === "Single") return [1];
    if (roomType === "Double") return [2];
    if (roomType === "Triple") return [3];
    return [1];
  };

  const getCNBOptions = (roomType, infantVal) => {
    if (!roomType) return ["N/A"];
    const infant = Number(infantVal) || 0;
    if (roomType === "Single") return [0, 1, 2];
    if (roomType === "Double") {
      if (infant === 1) return [0];
      return [0, 1];
    }
    if (roomType === "Triple") return [0, 1];
    return ["N/A"];
  };

  const getCWBOptions = (roomType) => {
    if (!roomType) return ["N/A"];
    if (roomType === "Single") return [0, 1];
    if (roomType === "Double") return [0, 1];
    if (roomType === "Triple") return ["N/A"];
    return ["N/A"];
  };

  const getInfantOptions = (roomType, cnbVal) => {
    if (!roomType) return [0];
    if (roomType === "Single") return [0, 1];
    if (roomType === "Double") return [0, 1, 2];
    if (roomType === "Triple") return [0, 1];
    return [0];
  };

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getCNBOptionsWithInfant = (roomType, infantValue) => {
    if (!roomType) return ["N/A"];
    const infant = Number(infantValue) || 0;
    switch (roomType) {
      case "Single":
        return [0, 1, 2];
      case "Double":
        if (infant === 1) return [0];
        return [0, 1];
      case "Triple":
        return [0, 1];
      default:
        return ["N/A"];
    }
  };

  const syncTravelers = (room) => {
    if (!room) return room;
    const adults = Number(room.adults) || 0;
    const cnb = room.cnb === "N/A" ? 0 : Number(room.cnb) || 0;
    const cwb = room.cwb === "N/A" ? 0 : Number(room.cwb) || 0;
    const infant = room.infant === "N/A" ? 0 : Number(room.infant) || 0;
    const totalPeople = adults + cnb + cwb + infant;
    const currentTravelers = room.travelers || [];
    if (currentTravelers.length === totalPeople) return room;

    let newTravelers = [...currentTravelers];
    if (totalPeople > currentTravelers.length) {
      const needed = totalPeople - currentTravelers.length;
      for (let i = 0; i < needed; i++) {
       newTravelers.push({
  firstName: "",
  lastName: "",
  dob: "",
  passport_expiry: "",
  gender: "",
  passport_issued: "",
  country: "",
  pan_no: "",
  travel_doc_no: "",
  contact_no: "",
});
      }
    } else {
      newTravelers = newTravelers.slice(0, totalPeople);
    }

    return { ...room, travelers: newTravelers };
  };

  const computeRoomsTotal = () => {
    let total = 0;
    if (!rooms) return total;
    rooms.forEach((r) => {
      const rateRow = findRateRow(form.hotel_rating, r.room_type);
      if (!rateRow) return;

      const adultPrice = parsePrice(rateRow.adults);
      const cnbPrice = parsePrice(rateRow.cnb);
      const cwbPrice = parsePrice(rateRow.cwb);
      const infantPrice = parsePrice(rateRow.infant);

      const adultsCount = Number(r.adults) || 0;
      const cnbCount = r.cnb === "N/A" ? 0 : Number(r.cnb) || 0;
      const cwbCount = r.cwb === "N/A" ? 0 : Number(r.cwb) || 0;
      const infantCount = r.infant === "N/A" ? 0 : Number(r.infant) || 0;

      total += adultsCount * adultPrice;
      total += cnbCount * cnbPrice;
      total += cwbCount * cwbPrice;
      total += infantCount * infantPrice;
    });
    return total;
  };

  useEffect(() => {
    const rTotal = computeRoomsTotal();
    setRoomsTotal(rTotal);

    const totalAdults =
      rooms?.reduce(
        (acc, r) => acc + (Number(r.adults) || 0),
        0
      ) || 0;

    const totalCNB =
      rooms?.reduce(
        (acc, r) =>
          acc + (r.cnb === "N/A" ? 0 : Number(r.cnb) || 0),
        0
      ) || 0;

    const totalCWB =
      rooms?.reduce(
        (acc, r) =>
          acc + (r.cwb === "N/A" ? 0 : Number(r.cwb) || 0),
        0
      ) || 0;

    const totalInfants =
      rooms?.reduce(
        (acc, r) =>
          acc + (r.infant === "N/A" ? 0 : Number(r.infant) || 0),
        0
      ) || 0;

    const totalFlightTravelers =
      totalAdults + totalCNB + totalCWB;

    const isFlightSelected =
      form.flight_option === "with_flight" ||
      isWithFlight;

    // =====================================
    // DEFAULT BASE PRICE
    // =====================================

    let defaultBasePrice = Number(doublePrice || 0);

    // Add flight price in default price
    if (
      isFlightSelected &&
      Number(pkg?.with_flight_regular_price || 0) > 0
    ) {
      defaultBasePrice += Number(
        pkg?.with_flight_regular_price || 0
      );
    }

    // =====================================
    // ROOM TOTAL
    // =====================================

    let roomTotal = rTotal;

    // If no room selected yet, show default package price
    if (roomTotal <= 0) {
      roomTotal = defaultBasePrice;
    }

    // =====================================
    // FLIGHT COST
    // =====================================
    // =====================================
    // FLIGHT COST
    // =====================================

    let flightTotalCost = 0;
    let infantFlightCharge = 0;

    if (isFlightSelected) {
      const flightRate = Number(pkg?.with_flight_regular_price || 0);

      // Update: Calculate flight cost for ALL flight-eligible travelers
      // No longer using Math.max(0, totalFlightTravelers - 1)
      flightTotalCost = flightRate * totalFlightTravelers;

      infantFlightCharge = totalInfants * (Number(pkg?.infant_flight_price) || 0);
    }

    // =====================================
    // FINAL BASE
    // =====================================

    const finalBase =
      roomTotal +
      flightTotalCost +
      infantFlightCharge;

    setBasePrice(finalBase);

    // =====================================
    // APPLY COUPON
    // =====================================

    // 1. Calculate taxes on the actual base price
const gst = finalBase * 0.05;
const tcs = (finalBase + gst) * 0.02; // TCS usually applies on amount including GST

// 2. Calculate the gross total
const grossTotal = finalBase + gst + tcs;

// 3. Apply discount at the end
const finalPayable = isCouponApplied 
  ? Math.max(0, grossTotal - discountAmount) 
  : grossTotal;

setGstAmount(gst);
setTcsAmount(tcs);
setCalculatedPrice(finalPayable);

  }, [
    rooms,
    form.hotel_rating,
    form.flight_option,
    withFlightTotalSalePrice,
    doublePrice,
    isWithFlight,
    isCouponApplied,
    discountAmount,
    pkg?.infant_flight_price,
    pkg?.with_flight_regular_price
  ]);


 const handleApplyCoupon = (coupon) => {
  if (!coupon) return;

 if (coupon.expiry_date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const expiryDate = new Date(coupon.expiry_date);
  expiryDate.setHours(23, 59, 59, 999);

  if (expiryDate < today) {
    setCouponMessage("Coupon expired");
    return;
  }
}

  if (coupon.usage_limit && coupon.used >= coupon.usage_limit) {
    setCouponMessage("Coupon usage limit reached");
    return;
  }

  if (coupon.expiry_date && new Date(coupon.expiry_date) < new Date()) {
    setCouponMessage("Coupon expired");
    return;
  }

  // Gross Total = Base + GST + TCS
  const gst = basePrice * 0.05;
  const tcs = (basePrice + gst) * 0.02;
  const grossTotal = basePrice + gst + tcs;

  let discount = 0;

  if (coupon.type === "fixed") {
    discount = Number(coupon.value);
  } else {
    discount = (grossTotal * Number(coupon.value)) / 100;
  }

  // Prevent discount from exceeding gross total
  discount = Math.min(discount, grossTotal);

  setCouponCode(coupon.code);
  setDiscountAmount(discount);
  setIsCouponApplied(true);
  setCouponMessage("Coupon applied successfully");
};

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setDiscountAmount(0);
    setIsCouponApplied(false);
    setCouponMessage("");
  };
  useEffect(() => {
    const availableTypes = getRoomTypesByRating(form.hotel_rating) || [];
    if (!availableTypes || availableTypes.length === 0) return;

    setRooms((prev) => {
      if (!prev) return [];
      return prev.map((r) => {
        if (r.room_type === "") return r;

        const valid = availableTypes.find((rt) => String(rt.room_type) === String(r.room_type));
        const chosenType = valid ? r.room_type : "";

        const adultsOpts = getAdultsOptions(chosenType);
        const adults = adultsOpts.includes(Number(r.adults)) ? String(r.adults) : String(adultsOpts[0]);

        const cnbOpts = getCNBOptions(chosenType, Number(adults));
        const cnb = cnbOpts.includes(Number(r.cnb)) ? String(r.cnb) : String(cnbOpts[0]);

        const cwbOpts = getCWBOptions(chosenType, cnb, Number(adults));
        const cwb = cwbOpts.includes(Number(r.cwb)) ? String(r.cwb) : String(cwbOpts[0]);

        const infOpts = getInfantOptions(chosenType, cnb);
        const infant = infOpts.includes(Number(r.infant)) ? String(r.infant) : String(infOpts[0]);

        const updatedRoom = {
          ...r,
          room_type: chosenType,
          adults,
          cnb,
          cwb,
          infant,
        };
        return syncTravelers(updatedRoom);
      });
    });
  }, [form.hotel_rating]);

  const handleRoomChange = (index, field, value) => {
    setRooms((prev) => {
      const copy = [...prev];
      let row = { ...(copy[index] || {}) };

      const roomAdultsMap = {
        Single: "1",
        Double: "2",
        Triple: "3",
      };

      let newRow =
        field === "room_type"
          ? (() => {
            const chosenType = value;
            if (!chosenType) {
              return {
                ...row,
                room_type: "",
                adults: "0",
                cnb: "N/A",
                cwb: "N/A",
                infant: "0",
                travelers: [],
              };
            }
            return {
              ...row,
              room_type: chosenType,
              adults: roomAdultsMap[chosenType] || "1",
              cnb: "0",
              cwb: chosenType === "Triple" ? "N/A" : "0",
              infant: "0",
            };
          })()
          : { ...row, [field]: value };

      if (newRow.room_type) {
        newRow.adults = roomAdultsMap[newRow.room_type] || "1";
      }

      if (newRow.room_type === "Triple") {
        newRow.cwb = "N/A";
      }

      if (!newRow.room_type) {
        newRow.cnb = "N/A";
        newRow.cwb = "N/A";
        newRow.infant = "0";
      }

      const validCNB = getCNBOptions(newRow.room_type, newRow.infant);
      if (!validCNB.includes(Number(newRow.cnb))) {
        newRow.cnb = String(validCNB[0]);
      }

      const validInf = getInfantOptions(newRow.room_type, newRow.cnb);
      if (!validInf.includes(Number(newRow.infant))) {
        newRow.infant = String(validInf[0]);
      }

      const validCWB = getCWBOptions(newRow.room_type);
      if (validCWB[0] === "N/A") {
        newRow.cwb = "N/A";
      }

      const adults = Number(newRow.adults) || 0;
      const cnb = newRow.cnb === "N/A" ? 0 : Number(newRow.cnb);
      const cwb = newRow.cwb === "N/A" ? 0 : Number(newRow.cwb);
      const infant = newRow.infant === "N/A" ? 0 : Number(newRow.infant);
      const totalPeople = adults + cnb + cwb + infant;

      let errorMsg = "";
      if (totalPeople > 4) {
        errorMsg = "Max 4 people allowed per room";
        setErrors((prev) => ({ ...prev, [`room_${index}_notice`]: errorMsg }));
        return prev;
      }

      if (newRow.room_type === "Single") {
        if (adults !== 1) errorMsg = "Single must have 1 adult";
        else if (cnb > 2) errorMsg = "Max 2 CNB allowed in Single";
        else if (cwb > 1) errorMsg = "Max 1 CWB allowed in Single";
        else if (infant > 1) errorMsg = "Max 1 Infant allowed in Single";
      }

      if (newRow.room_type === "Double") {
        if (adults !== 2) errorMsg = "Double must have 2 adults";
        else if (cnb > 1) errorMsg = "Max 1 CNB allowed in Double";
        else if (cwb > 1) errorMsg = "Max 1 CWB allowed in Double";
        else if (infant > 2) errorMsg = "Max 2 Infant allowed in Double";
        else if (cnb === 1 && infant === 1) {
          errorMsg = "CNB & Infant cannot both be 1 in Double";
        }
      }

      if (newRow.room_type === "Triple") {
        if (adults !== 3) errorMsg = "Triple must have 3 adults";
        else if (cnb > 1) errorMsg = "Max 1 CNB allowed in Triple";
        else if (cwb > 0) errorMsg = "CWB not allowed in Triple";
        else if (infant > 1) errorMsg = "Max 1 Infant allowed in Triple";
      }

      setErrors((prev) => ({ ...prev, [`room_${index}_notice`]: errorMsg }));
      copy[index] = syncTravelers(newRow);
      return copy;
    });
  };

  useEffect(() => {
    if (pkg?.hotel_ratings?.length > 0) {
      const firstRating = pkg.hotel_ratings[0].rating;
      setActiveRating(firstRating);
      setForm((prev) => ({ ...prev, hotel_rating: firstRating }));
    }
  }, [pkg]);

  const getTravelerLabel = (room, index) => {
    const labels = getTravelerLabels(room);
    return labels[index] || `Traveler ${index + 1}`;
  };

  const handleTravelerChange = (roomIndex, travelerIndex, field, value) => {
    setRooms((prev) => {
      const copy = [...prev];
      const room = { ...copy[roomIndex] };
      const travelers = [...(room.travelers || [])];

      const updatedTraveler = { ...travelers[travelerIndex], [field]: value };

      if (field === "dob") {
        const label = getTravelerLabel(room, travelerIndex);
        const age = calculateAge(value);
        let errorKey = `room_${roomIndex}_traveler_${travelerIndex}_dob`;
        let errorMsg = "";

        if (label.toLowerCase().includes("infant") && age > 2) {
          errorMsg = "Infant must be 2 years or below";
        }
        if (label.toLowerCase().includes("child") && age > 12) {
          errorMsg = "Child must be 12 years or below";
        }

        setErrors((prev) => ({ ...prev, [errorKey]: errorMsg }));
      }

      travelers[travelerIndex] = updatedTraveler;
      room.travelers = travelers;
      copy[roomIndex] = room;
      return copy;
    });
  };

  const addRoom = () => {
    const newRoom = {
      room_type: "",
      adults: "0",
      cnb: "N/A",
      cwb: "N/A",
      infant: "0",
      travelers: []
    };
    setRooms((prev) => [...(prev || []), newRoom]);
  };

  const removeRoom = (index) => {
    setRooms((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const [activeRating, setActiveRating] = useState(pkg?.hotel_ratings?.[0]?.rating || null);

  const validate = (currentStep = step) => {
    const newErrors = {};
    let isValid = true;

    if (currentStep === 1) {
      if (!form.booking_from) {
        newErrors.booking_from = "Check-in date is required";
        isValid = false;
      }

      rooms.forEach((room, index) => {
        if (!room.room_type) {
          newErrors[`room_${index}_type_required`] = `Please select a room type for Room ${index + 1}`;
          isValid = false;
        }

        // Run the custom block logic configuration checks during submission phase
        const adults = Number(room.adults) || 0;
        const cnb = room.cnb === "N/A" ? 0 : Number(room.cnb) || 0;
        const cwb = room.cwb === "N/A" ? 0 : Number(room.cwb) || 0;
        const infant = room.infant === "N/A" ? 0 : Number(room.infant) || 0;
        const totalPeople = adults + cnb + cwb + infant;

        if (totalPeople > 4) {
          newErrors[`room_${index}_notice`] = `Max 4 people allowed per room`;
          isValid = false;
        }

        if (room.room_type === "Single") {
          if (adults !== 1) newErrors[`room_${index}_notice`] = "Single must have 1 adult";
          else if (cnb > 2) newErrors[`room_${index}_notice`] = "Max 2 CNB allowed in Single";
          else if (cwb > 1) newErrors[`room_${index}_notice`] = "Max 1 CWB allowed in Single";
          else if (infant > 1) newErrors[`room_${index}_notice`] = "Max 1 Infant allowed in Single";
        }

        if (room.room_type === "Double") {
          if (adults !== 2) newErrors[`room_${index}_notice`] = "Double must have 2 adults";
          else if (cnb > 1) newErrors[`room_${index}_notice`] = "Max 1 CNB allowed in Double";
          else if (cwb > 1) newErrors[`room_${index}_notice`] = "Max 1 CWB allowed in Double";
          else if (infant > 2) newErrors[`room_${index}_notice`] = "Max 2 Infant allowed in Double";
          else if (cnb === 1 && infant === 1) {
            newErrors[`room_${index}_notice`] = "CNB & Infant cannot both be 1 in Double";
          }
        }

        if (room.room_type === "Triple") {
          if (adults !== 3) newErrors[`room_${index}_notice`] = "Triple must have 3 adults";
          else if (cnb > 1) newErrors[`room_${index}_notice`] = "Max 1 CNB allowed in Triple";
          else if (cwb > 0) newErrors[`room_${index}_notice`] = "CWB not allowed in Triple";
          else if (infant > 1) newErrors[`room_${index}_notice`] = "Max 1 Infant allowed in Triple";
        }

        if (newErrors[`room_${index}_notice`]) {
          isValid = false;
        }

      (room.travelers || []).forEach((t, tIdx) => {
  const label = getTravelerLabel(room, tIdx);
  const age = calculateAge(t.dob);
  // Define Lead Traveler: First room (index 0) and First traveler (tIdx 0)
  const isLead = index === 0 && tIdx === 0;

  // --- 1. Mandatory for ALL travelers ---
  const requiredForAll = [
    { key: "firstName", msg: "First name required" },
    { key: "lastName", msg: "Last name required" },
    { key: "dob", msg: "DOB required" },
    { key: "gender", msg: "Gender required" },
    { key: "passport_issued", msg: "Passport issued required" },
    { key: "passport_expiry", msg: "Passport expiry required" },
    { key: "country", msg: "Country required" },
    { key: "travel_doc_no", msg: "Doc number required" }
  ];

  requiredForAll.forEach(field => {
    if (!t[field.key]) {
      newErrors[`room_${index}_traveler_${tIdx}_${field.key}`] = field.msg;
      isValid = false;
    }
  });

  // --- 2. Mandatory for LEAD TRAVELER ONLY ---
  if (isLead) {
    if (!t.pan_no) {
      newErrors[`room_${index}_traveler_${tIdx}_pan_no`] = "PAN No required";
      isValid = false;
    }
    if (!t.contact_no) {
      newErrors[`room_${index}_traveler_${tIdx}_contact_no`] = "Contact No required";
      isValid = false;
    }
  }

  // --- 3. Existing Age Validations ---
  if (label.toLowerCase().includes("infant") && age > 2) {
    newErrors[`room_${index}_traveler_${tIdx}_dob`] = "Infant must be ≤ 2 years";
    isValid = false;
  }
  if (label.toLowerCase().includes("child") && age > 12) {
    newErrors[`room_${index}_traveler_${tIdx}_dob`] = "Child must be ≤ 12 years";
    isValid = false;
  }
});
      });
    }

    if (currentStep === 2) {
      if (!form.name.trim()) {
        newErrors.name = "Name is required";
        isValid = false;
      }
      if (!form.email.trim()) {
        newErrors.email = "Email is required";
        isValid = false;
      } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        newErrors.email = "Invalid email format";
        isValid = false;
      }
      if (!form.phone.trim()) {
        newErrors.phone = "Phone is required";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const getMaxDOB = (label) => {
    const today = new Date();
    if (label.toLowerCase().includes("infant")) {
      today.setFullYear(today.getFullYear() - 2);
    } else if (label.toLowerCase().includes("child")) {
      today.setFullYear(today.getFullYear() - 12);
    } else {
      return undefined;
    }
    return today.toISOString().split("T")[0];
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setServerMessage(null);
  if (!validate()) return;

  setLoading(true);

  try {
    let userId = null;
    let randomPassword = Math.random().toString(36).slice(-8);
    let storedUser = null;

    try {
      storedUser = JSON.parse(localStorage.getItem("user"));
    } catch (_) {}

    if (storedUser?.id) {
      userId = storedUser.id;
    } else {
      let existingUser = null;

      try {
        const lookup = await axios.get(
          `https://online.funforay.com/admin/public/api/users?email=${form.email}`
        );
        existingUser = lookup.data?.data?.[0] || null;
      } catch (err) {
        console.warn("User lookup failed. Continuing...");
      }

      if (existingUser) {
        userId = existingUser.id;
        localStorage.setItem("user", JSON.stringify(existingUser));
      } else {
        try {
          const register = await axios.post(
            "https://online.funforay.com/admin/public/api/register",
            {
              name: form.name,
              email: form.email,
              password: randomPassword,
              password_confirmation: randomPassword,
            }
          );

          userId = register.data?.user?.id;
          localStorage.setItem("user", JSON.stringify(register.data.user));

          await axios.post(
            "https://online.funforay.com/admin/mail/send-mail.php",
            qs.stringify({
              toname: form.name,
              toemail: form.email,
              password: randomPassword,
              subject: "Your FunForay Account Created",
              message: `Your account has been created. Password: ${randomPassword}`,
            }),
            { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
          );
        } catch (registerErr) {
          if (
            registerErr.response?.status === 422 &&
            registerErr.response?.data?.message === "The email has already been taken."
          ) {
            const again = await axios.get(
              `https://online.funforay.com/admin/public/api/users?email=${form.email}`
            );
            const userAgain = again.data?.data?.[0];

            if (!userAgain) {
              if (storedUser?.id) {
                userId = storedUser.id;
              } else {
                throw new Error("Email exists but user fetch failed.");
              }
            } else {
              userId = userAgain.id;
              localStorage.setItem("user", JSON.stringify(userAgain));
            }
          } else {
            throw registerErr;
          }
        }
      }
    }

    if (!userId) throw new Error("User ID missing. Cannot continue.");

    const bookingId = `#${Math.floor(Math.random() * 9000) + 1000}`;

    const formData = new FormData();
    formData.append("user_id", userId);
    formData.append("status", "0");
    formData.append("package_id", pkg?.id || "");
    formData.append("package_name", pkg?.title || "");
    formData.append("package_price", String(Math.round(calculatedPrice)));
    formData.append("booking_id", bookingId);
    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("hotel_rating", form.hotel_rating);
    formData.append("flight_option", form.flight_option);
    formData.append("booking_from", form.booking_from);
    formData.append("message", form.message);
    formData.append("coupon_applied", isCouponApplied ? 1 : 0);
    formData.append("coupon_code", isCouponApplied ? couponCode : "");
    formData.append("discount_amount", isCouponApplied ? Math.round(discountAmount) : 0);

    rooms.forEach((room, index) => {
      formData.append(`rooms[${index}][room_type]`, room.room_type);
      formData.append(`rooms[${index}][adults]`, room.adults);
      formData.append(`rooms[${index}][cnb]`, room.cnb);
      formData.append(`rooms[${index}][cwb]`, room.cwb);
      formData.append(`rooms[${index}][infant]`, room.infant);

      if (room.travelers && room.travelers.length > 0) {
        const labels = getTravelerLabels(room);
        room.travelers.forEach((traveler, tIdx) => {
          const label = labels[tIdx] || `Traveler ${tIdx + 1}`;
          formData.append(`rooms[${index}][travelers][${tIdx}][label]`, label);
          formData.append(`rooms[${index}][travelers][${tIdx}][first_name]`, traveler.firstName || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][last_name]`, traveler.lastName || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][dob]`, traveler.dob || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][gender]`, traveler.gender || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][passport_issued]`, traveler.passport_issued || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][passport_expiry]`, traveler.passport_expiry || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][country]`, traveler.country || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][pan_no]`, traveler.pan_no || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][travel_doc_no]`, traveler.travel_doc_no || "");
          formData.append(`rooms[${index}][travelers][${tIdx}][contact_no]`, traveler.contact_no || "");
        });
      }
    });

    // ===============================
    // ✅ BOOKING API CALL
    // ===============================
    await axios.post(
      "https://online.funforay.com/admin/public/api/package-booking",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    // =========================================================
    // 🚀 BREVO EMAIL INTEGRATION (ADDED - NO CORS ISSUES)
    // =========================================================
    try {
      const emailPayload = {
        booking_id: bookingId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        hotel_rating: form.hotel_rating,
        flight_option: form.flight_option,
        booking_from: form.booking_from,
        message: form.message,
        package_id: pkg?.id,
        package_name: pkg?.title,
        package_price: calculatedPrice,
        rooms: rooms
      };

      await axios.post(
        "https://online.funforay.com/admin/mail/send-booking-email.php",
        emailPayload,
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

    } catch (emailErr) {
      console.log("Brevo email failed but booking successful:", emailErr);
    }
    // =========================================================

    if (isCouponApplied) {
      await axios.post("https://online.funforay.com/admin/api/coupon/used", { code: couponCode });
    }

    setServerMessage({ type: "success", text: "Booking completed successfully!" });

    setTimeout(() => {
      window.location.href = "/booking";
    }, 1500);

  } catch (err) {
    console.error("SUBMIT ERROR:", err);
    setServerMessage({
      type: "error",
      text: err.response?.data?.message || err.message || "Server error, try again!",
    });
  } finally {
    setLoading(false);
  }
};
  const thStyle = { border: "1px solid #ddd", padding: 8, background: "#f8f8f8", textAlign: "center" };
  const tdStyle = { border: "1px solid #ddd", padding: 8, textAlign: "center" };
  const formatNumber = (n) => {
    if (n == null) return "0";
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const isFlightSelected = form.flight_option === "with_flight" || isWithFlight;
  const flightRateshow = isFlightSelected
    ? Number(pkg?.with_flight_regular_price ?? withFlightTotalSalePrice ?? 0)
    : 0;



  return (
    <div className="form_page">
      <form onSubmit={handleSubmit} className="popp_form">
        {serverMessage && (
          <div
            style={{
              position: "fixed",
              top: 20,
              right: 20,
              background: serverMessage.type === "success" ? "#28a745" : "#dc3545",
              color: "#fff",
              padding: "12px 18px",
              borderRadius: 6,
              zIndex: 9999,
            }}
          >
            {serverMessage.text}
          </div>
        )}

        <h2>{pkg?.title || ""}</h2>


        {/* <h3>
          Price: ₹{formatNumber(Math.round(calculatedPrice))}


        </h3> */}

        {step === 1 && (
          <>
            {pkg?.hotel_ratings?.length > 0 && (
              <div style={{ marginTop: 10, marginBottom: 18 }}>
                <h4><strong>Hotel Rating & Room Prices</strong></h4>

                <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
                  {pkg.hotel_ratings.map((hr, idx) => (
                    <button
                      type="button"
                      key={idx}
                      onClick={() => {
                        setActiveRating(hr.rating);
                        setForm((prev) => ({ ...prev, hotel_rating: hr.rating }));
                      }}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 6,
                        border: "1px solid #006cb8",
                        cursor: "pointer",
                        background: activeRating === hr.rating ? "#006cb8" : "#fff",
                        color: activeRating === hr.rating ? "#fff" : "#006cb8",
                        fontWeight: "600",
                      }}
                    >
                      {hr.rating} Star
                    </button>
                  ))}
                </div>

                {(pkg.hotel_ratings || [])
                  .filter(hr => hr.rating === activeRating)
                  .map((hr, idx) => {
                    const isWithFlightActive = form.flight_option === "with_flight" || isWithFlight;

                    // Always treat as a number, allowing 0 to be included
                    const rawFlightPrice = pkg?.with_flight_regular_price != null
                      ? pkg.with_flight_regular_price
                      : withFlightTotalSalePrice;

                    const flightFare = Number(rawFlightPrice || 0);

                    return (
                      <div key={idx}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }} className="price_table">
                          <thead>
                            <tr>
                              <th style={thStyle}>Room Type</th>
                              <th style={thStyle}>Adults</th>
                              <th style={thStyle}>CNB</th>
                              <th style={thStyle}>CWB</th>
                              <th style={thStyle}>Infant</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(hr.room_types || []).map((rt, j) => {
                              const baseAdult = parsePrice(rt.adults);
                              const baseCnb = parsePrice(rt.cnb);
                              const baseCwb = parsePrice(rt.cwb);
                              const baseInfant = parsePrice(rt.infant);
                              const infantflightFare = parsePrice(pkg?.infant_flight_price);

                              return (
                                <tr key={j}>
                                  <td style={tdStyle}>{rt.room_type}</td>

                                  {/* Adults Column: Always show total and "+ Flight" if active */}
                                  <td style={tdStyle}>
                                    {String(rt.adults).trim() === "NA" ? "N/A" : (
                                      <>
                                        ₹ {formatNumber(isWithFlightActive ? baseAdult + flightFare : baseAdult)}
                                        {/* {isWithFlightActive && (
                                          <span style={{ display: "block", fontSize: "10px", color: "#e2068c" }}>+ Flight</span>
                                        )} */}
                                      </>
                                    )}
                                  </td>

                                  {/* CNB Column: Always show total and "+ Flight" if active */}
                                  <td style={tdStyle}>
                                    {String(rt.cnb).trim() === "NA" ? "N/A" : (
                                      <>
                                        ₹ {formatNumber(isWithFlightActive ? baseCnb + flightFare : baseCnb)}
                                        {/* {isWithFlightActive && (
                                          <span style={{ display: "block", fontSize: "10px", color: "#e2068c" }}>+ Flight</span>
                                        )} */}
                                      </>
                                    )}
                                  </td>

                                  {/* CWB Column: Always show total and "+ Flight" if active */}
                                  <td style={tdStyle}>
                                    {String(rt.cwb).trim() === "NA" ? "N/A" : (
                                      <>
                                        ₹ {formatNumber(isWithFlightActive ? baseCwb + flightFare : baseCwb)}
                                        {/* {isWithFlightActive && (
                                          <span style={{ display: "block", fontSize: "10px", color: "#e2068c" }}>+ Flight</span>
                                        )} */}
                                      </>
                                    )}
                                  </td>

                                  {/* Infant Column */}
                                  <td style={tdStyle}>
                                    {String(rt.infant).trim() === "NA" || String(rt.infant).trim() === "N/A" ? (
                                      "N/A"
                                    ) : (
                                      <>
                                        ₹ {formatNumber(isWithFlightActive ? baseInfant + infantflightFare : baseInfant)}
                                        {/* {isWithFlightActive && (
                                          <span style={{ display: "block", fontSize: "10px", color: "#e2068c", marginTop: "2px" }}>
                                            + Flight
                                          </span>
                                        )} */}
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
              </div>
            )}

            <div className="room_sec_design">
              <div id="room-section" style={{ marginTop: 8 }}>
                {(rooms || []).map((r, idx) => {
                  const availableRoomTypes = getRoomTypesByRating(form.hotel_rating) || [];
                  const adultsOpts = getAdultsOptions(r.room_type);
                  const cnbOpts = getCNBOptionsWithInfant(r.room_type, r.infant);
                  const cwbOpts = getCWBOptions(r.room_type, r.cnb, Number(r.adults));
                  const infOpts = getInfantOptions(r.room_type, r.cnb);
                  const rateRow = findRateRow(form.hotel_rating, r.room_type);

                  // Dynamic flight context flag for calculations
                  const isWithFlightActive = form.flight_option === "with_flight" || isWithFlight;
                  const rawFlightPrice = pkg?.with_flight_regular_price != null ? pkg.with_flight_regular_price : withFlightTotalSalePrice;
                  const flightFare = Number(rawFlightPrice || 0);
                  const infantFlightFare = parsePrice(pkg?.infant_flight_price);

                  // Base hotel land prices computed dynamically per line row
                  const baseAdult = parsePrice(rateRow?.adults);
                  const baseCnb = parsePrice(rateRow?.cnb);
                  const baseCwb = parsePrice(rateRow?.cwb);
                  const baseInfant = parsePrice(rateRow?.infant);
                  // Display rates adding the flight component conditionally matching layout specifications
                  const displayAdultRate = isWithFlightActive ? (baseAdult + flightFare) : baseAdult;
                  const displayCnbRate = isWithFlightActive ? (baseCnb + flightFare) : baseCnb;
                  const displayCwbRate = isWithFlightActive ? (baseCwb + flightFare) : baseCwb;
                  const displayInfantRate = isWithFlightActive ? (baseInfant + infantFlightFare) : baseInfant;

                  const adultCount = Number(r.adults) || 0;
                  const cnbCount = r.cnb === "N/A" ? 0 : Number(r.cnb) || 0;
                  const cwbCount = r.cwb === "N/A" ? 0 : Number(r.cwb) || 0;
                  const infantCount = r.infant === "N/A" ? 0 : Number(r.infant) || 0;

                  const rowTotal = (adultCount * displayAdultRate) +
                    (cnbCount * displayCnbRate) +
                    (cwbCount * displayCwbRate) +
                    (infantCount * displayInfantRate);

                  return (
                    <div
                      key={idx}
                      className="room-block"
                      style={{ border: "1px solid #eee", padding: 10, marginBottom: 10, position: "relative" }}
                    >
                      {rooms.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRoom(idx)}
                          style={{ position: "absolute", right: 8, top: 8 }}
                          className="btn btn-danger btn-sm"
                        >
                          Remove
                        </button>
                      )}
                      <h5>Room {idx + 1}</h5>

                      <div className="row rprice flex select_style">
                        <div style={{ flex: "0 0 240px", padding: 6 }}>
                          <label>Room Type</label>
                          <select
                            className="form-control room-type"
                            value={r.room_type}
                            onChange={(e) => handleRoomChange(idx, "room_type", e.target.value)}
                            required
                          >
                            <option value="">Select Room Type</option>
                            {availableRoomTypes.map((rt, i) => {
                              // Consistent pricing context
                              const isWithFlightActive = form.flight_option === "with_flight" || isWithFlight;
                              const rawFlightPrice = pkg?.with_flight_regular_price != null
                                ? pkg.with_flight_regular_price
                                : withFlightTotalSalePrice;
                              const flightFare = Number(rawFlightPrice || 0);

                              // Get the base adult rate from the current room type object
                              const baseAdultPrice = parsePrice(rt.adults);

                              // Calculate display price (matching the logic in your hotel ratings table)
                              const displayPrice = isWithFlightActive ? (baseAdultPrice + flightFare) : baseAdultPrice;

                              return (
                                <option key={i} value={rt.room_type}>
                                  {rt.room_type}
                                  {rt.adults && String(rt.adults).trim().toLowerCase() !== "n/a"
                                    ? ` — ₹${formatNumber(displayPrice)}${isWithFlightActive ? ' (Inc. Flight)' : ''}`
                                    : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        <div style={{ flex: "0 0 120px", padding: 6 }}>
                          <label>Adults</label>
                          <select disabled={!r.room_type}
                            className="form-control adults"
                            value={r.adults}
                            onChange={(e) => handleRoomChange(idx, "adults", e.target.value)}
                            required
                          >
                            {!r.room_type ? <option value="0">0</option> : adultsOpts.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ flex: "0 0 120px", padding: 6 }}>
                          <label>CNB</label>
                          <select disabled={!r.room_type || cnbOpts[0] === "N/A"}
                            className="form-control cnb"
                            value={r.cnb}
                            onChange={(e) => handleRoomChange(idx, "cnb", e.target.value)}
                            required
                          >
                            {cnbOpts.map((o) => (
                              <option key={String(o)} value={String(o)}>
                                {String(o)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ flex: "0 0 120px", padding: 6 }}>
                          <label>CWB</label>
                          <select disabled={!r.room_type || cwbOpts[0] === "N/A"}
                            className="form-control cwb"
                            value={r.cwb}
                            onChange={(e) => handleRoomChange(idx, "cwb", e.target.value)}
                            required
                          >
                            {cwbOpts.map((o) => (
                              <option key={String(o)} value={String(o)}>
                                {String(o)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ flex: "0 0 120px", padding: 6 }}>
                          <label>Infant</label>
                          <select disabled={!r.room_type || infOpts[0] === "N/A"}
                            className="form-control infant"
                            value={r.infant}
                            onChange={(e) => handleRoomChange(idx, "infant", e.target.value)}
                            required
                          >
                            {infOpts.map((o) => (
                              <option key={String(o)} value={String(o)}>
                                {String(o)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* ADDED ROOM NOTICE / ERROR BLOCK DISPLAY */}
                      {errors[`room_${idx}_notice`] && (
                        <div
                          className="room-error-notice"
                          style={{
                            margin: "8px 6px",
                            padding: "8px 12px",
                            background: "#fff3cd",
                            color: "#856404",
                            border: "1px solid #ffeeba",
                            borderRadius: "4px",
                            fontSize: "13px",
                            fontWeight: "500"
                          }}
                        >
                          ⚠️ {errors[`room_${idx}_notice`]}
                        </div>
                      )}

                      {/* DYNAMIC ROOM PRICE BREAKDOWN CARD */}
                      <div className="row_styledesigns">

                        <div className="rows_total">
                          <div>Adult <span>{String(rateRow?.adults).trim() === "NA" ? "N/A" : `₹${formatNumber(displayAdultRate)} × ${adultCount}`}</span></div>
                          <div>CNB <span>{r.cnb === "N/A" || String(rateRow?.cnb).trim() === "NA" ? "N/A" : `₹${formatNumber(displayCnbRate)} × ${cnbCount}`}</span></div>
                          <div>CWB <span>{r.cwb === "N/A" || String(rateRow?.cwb).trim() === "NA" ? "N/A" : `₹${formatNumber(displayCwbRate)} × ${cwbCount}`}</span></div>
                          <div>Infant <span>{r.infant === "N/A" || String(rateRow?.infant).trim() === "NA" ? "N/A" : `₹${formatNumber(displayInfantRate)} × ${infantCount}`}</span></div>
                        </div>
                        <div style={{ fontSize: 20, color: "#fff" }}>
                          <strong>Total:</strong> ₹{formatNumber(Math.round(rowTotal))}
                        </div>
                      </div>


                      {r.room_type && r.travelers && r.travelers.length > 0 && (
  <div style={{ marginTop: 15, background: "#f9f9f9", padding: 10, borderRadius: 5 }}>
    <h5 style={{ margin: "0 0 10px 0", borderBottom: "1px solid #ddd", paddingBottom: 5 }}>
      Traveler Details ({r.travelers?.length || 0} Person(s))
    </h5>

    {r.travelers.map((traveler, tIdx) => {
      // Calculate if this is the lead traveler (first room, first person)
      const isLead = idx === 0 && tIdx === 0;
      
      return (
        <div key={tIdx} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: "1px dashed #ccc" }}>
          <strong>
            {getTravelerLabel(r, tIdx)} {isLead && "(Lead Traveler)"}
          </strong>
          
          <div className="trevel_form">
            {/* Standard Fields (Mandatory for ALL) */}
            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>First Name *</label>
              <input
                type="text"
                placeholder="First Name"
                className="form-control"
                value={traveler.firstName || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "firstName", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_firstName`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_firstName`]}</span>
              )}
            </div>

            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Last Name *</label>
              <input
                type="text"
                placeholder="Last Name"
                className="form-control"
                value={traveler.lastName || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "lastName", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_lastName`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_lastName`]}</span>
              )}
            </div>

            <div style={{ flex: "1 1 130px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>DOB *</label>
              <input
                type="date"
                className="form-control"
                value={traveler.dob || ""}
                max={getMaxDOB(getTravelerLabel(r, tIdx))}
                onChange={(e) => handleTravelerChange(idx, tIdx, "dob", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_dob`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_dob`]}</span>
              )}
            </div>

            <div style={{ flex: "0 0 100px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Gender *</label>
              <select
                className="form-control"
                value={traveler.gender || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "gender", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors[`room_${idx}_traveler_${tIdx}_gender`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_gender`]}</span>
              )}
            </div>

            <div style={{ flex: "1 1 130px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Passport Exp. *</label>
              <input
                type="date"
                className="form-control"
                value={traveler.passport_expiry || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "passport_expiry", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_passport_expiry`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_passport_expiry`]}</span>
              )}
            </div>
             {/* Passport Issued */}
<div style={{ flex: "1 1 130px" }}>
  <label style={{ fontSize: 11, display: 'block' }}>Passport Issued *</label>
  <input
    type="date"
    className="form-control"
    required
    value={traveler.passport_issued || ""}
    onChange={(e) => handleTravelerChange(idx, tIdx, "passport_issued", e.target.value)}
  />
  {errors[`room_${idx}_traveler_${tIdx}_passport_issued`] && (
    <span style={{ color: "red", fontSize: "11px", display: "block", marginTop: "4px" }}>
      {errors[`room_${idx}_traveler_${tIdx}_passport_issued`]}
    </span>
  )}
</div>
            <div style={{ flex: "1 1 130px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Country *</label>
              <input
                type="text"
                placeholder="Country"
                className="form-control"
                value={traveler.country || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "country", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_country`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_country`]}</span>
              )}
            </div>

            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Travel Doc No. *</label>
              <input
                type="text"
                placeholder="Passport / [ID Redacted] No"
                className="form-control"
                value={traveler.travel_doc_no || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "travel_doc_no", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_travel_doc_no`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_travel_doc_no`]}</span>
              )}
            </div>

            {/* Lead Traveler Mandatory Fields */}
            <div style={{ flex: "1 1 120px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>PAN No. {isLead && "*"}</label>
              <input
                type="text"
                placeholder="PAN No"
                className="form-control"
                value={traveler.pan_no || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "pan_no", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_pan_no`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_pan_no`]}</span>
              )}
            </div>

            <div style={{ flex: "1 1 140px" }}>
              <label style={{ fontSize: 11, display: 'block' }}>Contact No. (India) {isLead && "*"}</label>
              <input
                type="tel"
                placeholder="Phone Number"
                className="form-control"
                value={traveler.contact_no || ""}
                onChange={(e) => handleTravelerChange(idx, tIdx, "contact_no", e.target.value)}
              />
              {errors[`room_${idx}_traveler_${tIdx}_contact_no`] && (
                <span style={{ color: "red", fontSize: "11px", display: "block" }}>{errors[`room_${idx}_traveler_${tIdx}_contact_no`]}</span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
)}
                    </div>
                  );
                })}
              </div>

              <div style={{ marginBottom: 12 }}>
                <button type="button" onClick={addRoom} className="btn btn-sm btn-secondary mt-2">
                  Add Room
                </button>
              </div>
            </div>

            <div className="form_inputs_style">
              <div style={{ marginTop: 10 }}>
                <label style={{ marginBottom: 10 }}>
                  <strong>Choose Flight Option:</strong>
                </label>
                <br />
                <label style={{ marginRight: 12 }}>
                  <input
                    disabled
                    type="radio"
                    name="flight_option"
                    value="with_flight"
                    checked={form.flight_option === "with_flight"}
                    onChange={(e) => setForm((prev) => ({ ...prev, flight_option: e.target.value }))}
                  />
                  With Flight
                </label>

                <label>
                  <input
                    disabled
                    type="radio"
                    name="flight_option"
                    value="without_flight"
                    checked={form.flight_option === "without_flight"}
                    onChange={(e) => setForm((prev) => ({ ...prev, flight_option: e.target.value }))}
                  />
                  Without Flight
                </label>
              </div>

              <div className="datess">
                <div style={{ marginTop: 10 }}>
                  <label>From Date</label>
                  <input
                    type="date"
                    name="booking_from"
                    value={form.booking_from}
                    onChange={handleChange}
                    className="form-control valid_date"
                    required
                    min={pkg?.available_from ? new Date(pkg.available_from).toISOString().split("T")[0] : undefined}
                    max={pkg?.available_to ? new Date(pkg.available_to).toISOString().split("T")[0] : undefined}
                  />
                  {errors.booking_from && (
                    <span style={{ color: "red", fontSize: "12px" }}>{errors.booking_from}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="fine_total">
              <button
                type="button"
                className="btn-primary submitt_btn"
                onClick={() => {
                  if (validate(1)) {
                    setStep(2);
                  } else {
                    setServerMessage({
                      type: "error",
                      text: "Complete traveler details.",
                    });
                  }
                }}
              >
                Next Step
              </button>


            </div>

          </>
        )}

        {step === 2 && (
          <>
            <label>Your Name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              disabled={!!JSON.parse(localStorage.getItem("user") || "{}")?.id}
            />
            {errors.name && <span style={{ color: "red", fontSize: "12px" }}>{errors.name}</span>}

            <label>Your Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              disabled={!!JSON.parse(localStorage.getItem("user") || "{}")?.id}
            />
            {errors.email && <span style={{ color: "red", fontSize: "12px" }}>{errors.email}</span>}

            <label>Your Phone</label>
            <input type="tel" name="phone" value={form.phone} onChange={handleChange} />
            {errors.phone && <span style={{ color: "red", fontSize: "12px" }}>{errors.phone}</span>}

            <label>Message (optional)</label>
            <textarea name="message" value={form.message} onChange={handleChange} rows="3" />

            <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
              <button type="button" className="btn-secondary submitt_btn" onClick={() => setStep(1)}>
                Previous Step
              </button>
              <button type="submit" className="submitt_btn" disabled={loading}>
                {loading ? "Submitting..." : `Submit (₹${formatNumber(Math.round(calculatedPrice))})`}
              </button>
            </div>
          </>
        )}

        <div className="coupans detailscoupon">

          {/* PRICE BREAKDOWN */}
          <div className="price_breakdown_card">
            <div className="price_breakdown_header">
              <h4>Price Breakdown</h4>
            </div>

            <ul className="price_breakdown_list">

              {/* LAND COST */}
              <li>
                <span>Selected Rooms Land Cost</span>

                <strong>
                  ₹{formatNumber(Math.round(roomsTotal))}
                </strong>
              </li>

              {/* FLIGHT DETAILS */}
             {(form.flight_option === "with_flight" || isWithFlight) &&
  (() => {
    const totalAdults = rooms?.reduce((acc, r) => acc + (Number(r.adults) || 0), 0) || 0;
    const totalCNB = rooms?.reduce((acc, r) => acc + (r.cnb === "N/A" ? 0 : Number(r.cnb) || 0), 0) || 0;
    const totalCWB = rooms?.reduce((acc, r) => acc + (r.cwb === "N/A" ? 0 : Number(r.cwb) || 0), 0) || 0;
    const totalInfants = rooms?.reduce((acc, r) => acc + (r.infant === "N/A" ? 0 : Number(r.infant) || 0), 0) || 0;

    const totalFlightTravelers = totalAdults + totalCNB + totalCWB;

    const flightFare = Number(pkg?.with_flight_regular_price || 0);
    const infantFlightFare = parsePrice(pkg?.infant_flight_price) || 0;

    // Calculate total cost for all travelers
    const flightTotal = totalFlightTravelers * flightFare;
    const infantTotal = totalInfants * infantFlightFare;

    return (
      <>
        {totalFlightTravelers > 0 && flightTotal > 0 && (
          <li>
            <span>Flight Fare ({totalFlightTravelers} Travelers)</span>
            <strong>
              ₹{formatNumber(Math.round(flightTotal))}
            </strong>
          </li>
        )}

        {totalInfants > 0 && infantTotal > 0 && (
          <li>
            <span>Infant Flight Surcharge ({totalInfants})</span>
            <strong>
              ₹{formatNumber(Math.round(infantTotal))}
            </strong>
          </li>
        )}
      </>
    );
  })()}

              {/* SUBTOTAL */}
              <li>
                <span>Subtotal</span>

                <strong>
                  ₹
                  {formatNumber(
                    Math.round(basePrice)
                  )}
                </strong>
              </li>






              {/* GST */}
              <li>
                <span>GST (5%)</span>

                <strong>
                  ₹
                  {formatNumber(
                    Math.round(gstAmount)
                  )}
                </strong>
              </li>

              {/* TCS */}
              <li className="tcs_row">
                <span>TCS (2%)</span>

                <strong>
                  ₹
                  {formatNumber(
                    Math.round(tcsAmount)
                  )}
                </strong>
              </li>

              <div className="price_divider"></div>
              {isCouponApplied && discountAmount > 0 && (
                <li className="discount_row">
                  <span>You Saved with Coupon ({couponCode})</span>
                  <strong style={{ color: "green" }}>
                    - ₹{formatNumber(Math.round(discountAmount))}
                  </strong>
                </li>
              )}
              {/* FINAL */}
              <li className="final_price_row">
                <span>Final Payable Amount</span>

                <strong>
                  ₹
                  {formatNumber(
                    Math.round(
                      calculatedPrice
                    )
                  )}
                </strong>
              </li>
            </ul>
          </div>

          {/* COUPONS */}
          <h4 className="coupon_title">
            Coupons & Offers
          </h4>

          <div className="coupon_list listcoupans">
            {(coupons || [])
              .filter((c) => c?.status)
              .slice(0, 2)
              .map((c, i) => {
                const isActive = couponCode === c.code;

                return (
                  <div
                    key={i}
                    className={`coupon_card ${isActive ? "active" : ""
                      }`}
                  >
                    <div className="coupon_left">
                      <div className="coupon_icon">
                        {c.type === "percentage" ? (
                          <img src="/images/percent.png" alt="Percentage Icon" />
                        ) : (
                          <img src="/images/rupee-indian.png" alt="Rupee Icon" />
                        )}
                      </div>

                      <div className="coupon_content">
                        <div className="csdesign"> <h5>{c.code}</h5>
                          (<span className="coupon_discount">

                            {c.type === "percentage"
                              ? `${c.value}%`
                              : `₹${c.value}`}
                            &nbsp;&nbsp;Off</span>)
                        </div>
                        <p>
                          Get{" "}
                          {c.type === "percentage"
                            ? `${c.value}%`
                            : `₹${c.value}`}{" "}
                          instant discount on booking.
                        </p>
                        {!isActive ? (
                          <button
                            type="button"
                            className="remove_coupon_btn"
                            onClick={() =>
                              handleApplyCoupon(c)
                            }
                          >
                            APPLY
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="remove_coupon_btn active_btn"
                            onClick={handleRemoveCoupon}
                          >
                            REMOVE
                          </button>
                        )}
                      </div>
                    </div>


                  </div>
                );
              })}
          </div>

          {/* MESSAGE */}
          {couponMessage && (
            <p
              style={{
                color: isCouponApplied
                  ? "green"
                  : "red",
                marginTop: "10px",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {couponMessage}
            </p>
          )}
        </div>
        <p>Note: For International travel, TCS 2% will be extra on total amount received to us. However, you can claim it while filing Income Tax Return</p>
      </form>


    </div>
  );
}

export default PackageBookingPopup;